import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useBack } from '../lib/back.js';
import type { ScratchSlot, ScratchWindowDetail, ScratchPerson } from '@keetcheck/shared';
import { theme } from '../theme.js';
import { useAuth } from '../context/AuthContext.js';
import {
  useScratchWindow, useScratchWindows, useSaveAvailability, useSaveScratchDraft, useScratchAction,
  useCreateScratchWindow, useDutyRoster,
} from '../lib/queries.js';
import { PageShell } from '../components/PageShell.js';
import { Icon } from '../components/Icon.js';
import { CapabilityStrip, Pill } from '../components/CoverBits.js';
import { emitError } from '../lib/toastBus.js';
import { dayLabel, timeRange, localDay, isDayShift, shortDate, addDays } from '../lib/shifts.js';

const fmtDate = (iso: string) => new Date(iso).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });

// ── The window page ────────────────────────────────────────────

export function ScratchPage() {
  const nav = useNavigate();
  const back = useBack('/shifts');
  const { id = '' } = useParams();
  const { user } = useAuth();
  const orgId = user?.orgId ?? '';
  const { data: w, isLoading, error } = useScratchWindow(orgId, id);
  const [tab, setTab] = useState<'mine' | 'build'>('mine');

  return (
    <PageShell noNav>
      <div style={{ padding: '12px 16px', background: theme.surface, borderBottom: `1px solid ${theme.border}`, position: 'sticky', top: 0, zIndex: 5 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button onClick={back} aria-label="Back" style={iconBtn}><Icon name="back" size={18} /></button>
          <div style={{ fontSize: 17, fontWeight: 700, color: theme.text, flex: 1, minWidth: 0 }}>{w?.title ?? 'Scratch weeks'}</div>
        </div>
        {w?.isAdmin && (
          <div role="tablist" style={{ display: 'flex', gap: 4, background: theme.surface2, padding: 3, borderRadius: 10, marginTop: 10 }}>
            {([['mine', 'My availability'], ['build', 'Build roster']] as const).map(([t, label]) => (
              <button key={t} role="tab" aria-selected={tab === t} onClick={() => setTab(t)} style={{
                flex: 1, padding: '7px 0', borderRadius: 8, border: 'none', cursor: 'pointer', fontFamily: theme.font, fontSize: 13, fontWeight: 700,
                background: tab === t ? theme.surface : 'transparent', color: tab === t ? theme.text : theme.textMuted,
              }}>{label}</button>
            ))}
          </div>
        )}
      </div>
      <div style={{ flex: 1, overflowY: 'auto', background: theme.bg, padding: '12px 16px 32px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {isLoading && <div style={muted}>Loading…</div>}
        {error && <div style={muted}>{(error as Error).message}</div>}
        {w && (tab === 'build' && w.isAdmin ? <Builder orgId={orgId} w={w} /> : <Availability orgId={orgId} w={w} />)}
      </div>
    </PageShell>
  );
}

// ── Members: tick shifts ───────────────────────────────────────

function Availability({ orgId, w }: { orgId: string; w: ScratchWindowDetail }) {
  const save = useSaveAvailability(orgId, w.id);
  const [picked, setPicked] = useState<Set<string>>(() => new Set(w.slots.filter((s) => s.myAvailable).map((s) => s.shiftId)));
  const [dirty, setDirty] = useState(false);
  useEffect(() => { if (!dirty) setPicked(new Set(w.slots.filter((s) => s.myAvailable).map((s) => s.shiftId))); }, [w, dirty]);
  const future = w.slots.filter((s) => new Date(s.startsAt) > new Date());
  const days = [...new Set(future.map((s) => localDay(s.startsAt)))];
  const toggle = (id: string) => { const n = new Set(picked); if (n.has(id)) n.delete(id); else n.add(id); setPicked(n); setDirty(true); };

  return (
    <>
      <div style={{ ...card, fontSize: 13, color: theme.textDim, lineHeight: 1.5 }}>
        No teams are rostered {shortDate(w.fromWeek)} – {shortDate(addDays(w.toWeek, 7))}. Tick every shift you <strong>could</strong> do —
        the roster is built from everyone's answers, spread fairly. Reply by <strong>{fmtDate(w.replyBy)}</strong>, even if it's none.
        {w.myReplied && <div style={{ marginTop: 6 }}><Pill tone="ok">Replied · {w.myAvailableCount} shift{w.myAvailableCount === 1 ? '' : 's'}</Pill></div>}
        {w.publishedAt && <div style={{ marginTop: 6 }}><Pill tone="service">Roster published — see My shifts</Pill></div>}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={() => { setPicked(new Set(future.map((s) => s.shiftId))); setDirty(true); }} style={secondarySm}>All of them</button>
        <button onClick={() => { setPicked(new Set()); setDirty(true); }} style={secondarySm}>None</button>
      </div>
      <div style={{ ...card, padding: 0 }}>
        {days.map((d) => (
          <div key={d} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderBottom: `1px solid ${theme.border}` }}>
            <div style={{ width: 92, fontSize: 13, fontWeight: 700, color: theme.text }}>
              {dayLabel(future.find((s) => localDay(s.startsAt) === d)!.startsAt)}
              {future.find((s) => localDay(s.startsAt) === d && s.holiday) && <div style={{ fontSize: 10, color: theme.status.service.fg, fontWeight: 600 }}>{future.find((s) => localDay(s.startsAt) === d && s.holiday)!.holiday}</div>}
            </div>
            {future.filter((s) => localDay(s.startsAt) === d).map((s) => (
              <button key={s.shiftId} onClick={() => toggle(s.shiftId)} aria-pressed={picked.has(s.shiftId)} style={{
                flex: 1, padding: '8px 4px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: theme.font,
                border: `1px solid ${picked.has(s.shiftId) ? theme.accent : theme.border}`,
                background: picked.has(s.shiftId) ? theme.accent : theme.surface, color: picked.has(s.shiftId) ? theme.accentInk : theme.textDim,
              }}>
                {isDayShift(s.startsAt) ? 'Day' : 'Night'}<br /><span style={{ fontWeight: 500 }}>{timeRange(s.startsAt, s.endsAt)}</span>
              </button>
            ))}
          </div>
        ))}
      </div>
      <button disabled={save.isPending} onClick={() => save.mutate([...picked], {
        onSuccess: () => setDirty(false), onError: (e) => emitError(e instanceof Error ? e.message : 'Could not save'),
      })} style={primary}>
        {save.isPending ? 'Saving…' : `Save — ${picked.size} shift${picked.size === 1 ? '' : 's'}`}
      </button>
    </>
  );
}

// ── Admins: build the roster ───────────────────────────────────

function Builder({ orgId, w }: { orgId: string; w: ScratchWindowDetail }) {
  const act = useScratchAction(orgId, w.id);
  const draft = useSaveScratchDraft(orgId, w.id);
  const slots = w.slots.filter((s) => new Date(s.startsAt) > new Date());
  const errors = slots.filter((s) => s.flags?.some((f) => f.level === 'error')).length;
  const exceptions = slots.filter((s) => s.flags?.some((f) => /exception/.test(f.text))).length;
  const run = (a: 'suggest' | 'publish' | 'remind') => act.mutate(a, {
    onSuccess: (r) => { if (a === 'remind') emitError(`Reminded ${(r as { reminded: number }).reminded} people`); },
    onError: (e) => emitError(e instanceof Error ? e.message : 'Something went wrong'),
  });
  const saveSlot = (s: ScratchSlot, udoUserId: string | null, crew: string[]) =>
    draft.mutate([{ shiftId: s.shiftId, udoUserId, crew }], { onError: (e) => emitError(e instanceof Error ? e.message : 'Could not save') });

  return (
    <>
      <div style={card}>
        <div style={{ fontSize: 13, color: theme.textDim }}>
          <strong>{w.replies?.replied.length ?? 0}</strong> replied · <strong>{w.replies?.notReplied.length ?? 0}</strong> haven't
          {' '}· reply by {fmtDate(w.replyBy)}
        </div>
        {!!w.replies?.notReplied.length && (
          <div style={{ fontSize: 12, color: theme.textMuted, marginTop: 4 }}>Waiting on: {w.replies.notReplied.map((p) => p.displayName).join(', ')}</div>
        )}
        <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
          <button disabled={act.isPending} onClick={() => run('suggest')} style={secondarySm}>Suggest (fill empty spots)</button>
          {!!w.replies?.notReplied.length && <button disabled={act.isPending} onClick={() => run('remind')} style={secondarySm}>Remind</button>}
          <button disabled={act.isPending || !w.unpublishedChanges} onClick={() => {
            if (confirm(errors ? `${errors} shift(s) still have problems. Publish anyway?` : 'Publish? Everyone affected is told.')) run('publish');
          }} style={{ ...primarySm, opacity: w.unpublishedChanges ? 1 : 0.5 }}>
            {w.publishedAt ? (w.unpublishedChanges ? 'Publish changes' : 'Published') : 'Publish roster'}
          </button>
        </div>
        <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
          {errors > 0 && <Pill tone="danger">{errors} with problems</Pill>}
          {exceptions > 0 && <Pill tone="warn">{exceptions} on 2 (exception)</Pill>}
          {w.unpublishedChanges && <Pill tone="service">Unpublished changes</Pill>}
        </div>
      </div>
      {slots.map((s) => <SlotCard key={s.shiftId} s={s} save={saveSlot} busy={draft.isPending} />)}
    </>
  );
}

function SlotCard({ s, save, busy }: { s: ScratchSlot; save: (s: ScratchSlot, udo: string | null, crew: string[]) => void; busy: boolean }) {
  const d = s.draft ?? { udoUserId: null, crew: [] };
  const avail = s.available ?? [];
  const name = (u: string | null) => avail.find((p) => p.userId === u)?.displayName ?? u ?? '';
  const crewNames = d.crew.map((u) => ({ u, p: avail.find((x) => x.userId === u) }));
  const addable = avail.filter((p) => p.operational && !d.crew.includes(p.userId) && p.userId !== d.udoUserId);
  const udoChoices = avail.filter((p) => p.udo && !d.crew.includes(p.userId));
  const label = (p: ScratchPerson) => `${p.displayName}${p.quals.length ? ` · ${p.quals.join(' ')}` : ''} · ${p.assigned} so far`;
  const worst = s.flags?.some((f) => f.level === 'error') ? 'danger' : s.flags?.length ? 'warn' : 'ok';

  return (
    <div style={{ ...card, borderLeft: `4px solid ${theme.status[worst].dot}` }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: theme.text, flex: 1 }}>
          {dayLabel(s.startsAt)} · {isDayShift(s.startsAt) ? 'Day' : 'Night'} <span style={{ fontWeight: 500, color: theme.textMuted, fontSize: 12 }}>{timeRange(s.startsAt, s.endsAt)}</span>
        </div>
        <span style={{ fontSize: 11, color: theme.textMuted }}>{s.availableCount} available</span>
      </div>
      {s.holiday && <div style={{ fontSize: 11, color: theme.status.service.fg }}>{s.holiday}</div>}
      {!!s.flags?.length && (
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', margin: '6px 0' }}>
          {s.flags.map((f) => <Pill key={f.text} tone={f.level === 'error' ? 'danger' : 'warn'}>{f.text}</Pill>)}
        </div>
      )}
      {s.udoShiftId && (
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, margin: '6px 0' }}>
          <span style={{ width: 40, fontWeight: 700, color: theme.text }}>UDO</span>
          <select disabled={busy} value={d.udoUserId ?? ''} onChange={(e) => save(s, e.target.value || null, d.crew)} aria-label="UDO" style={{ ...select, flex: 1 }}>
            <option value="">— nobody</option>
            {d.udoUserId && !udoChoices.some((p) => p.userId === d.udoUserId) && <option value={d.udoUserId}>{name(d.udoUserId)}</option>}
            {udoChoices.map((p) => <option key={p.userId} value={p.userId}>{label(p)}</option>)}
          </select>
        </label>
      )}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', margin: '6px 0' }}>
        <span style={{ width: 40, fontSize: 13, fontWeight: 700, color: theme.text }}>Crew</span>
        {crewNames.map(({ u, p }) => (
          <span key={u} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 4px 3px 8px', borderRadius: 999, background: theme.surface2, fontSize: 12, fontWeight: 600, color: theme.text }}>
            {p?.displayName ?? u}{p?.quals.length ? <span style={{ color: theme.textMuted, fontWeight: 500 }}>{p.quals.join(' ')}</span> : null}
            <button disabled={busy} onClick={() => save(s, d.udoUserId, d.crew.filter((x) => x !== u))} aria-label={`Remove ${p?.displayName ?? u}`} style={chipX}>
              <Icon name="x" size={10} />
            </button>
          </span>
        ))}
        {addable.length > 0 && (
          <select disabled={busy} value="" onChange={(e) => e.target.value && save(s, d.udoUserId, [...d.crew, e.target.value])} aria-label="Add crew" style={{ ...select, flex: '1 1 140px' }}>
            <option value="">+ Add…</option>
            {addable.map((p) => <option key={p.userId} value={p.userId}>{label(p)}</option>)}
          </select>
        )}
      </div>
      {s.capability && d.crew.length > 0 && <CapabilityStrip cap={s.capability} compact />}
    </div>
  );
}

// ── Roster tab card: open a window / list ──────────────────────

export function ScratchWindowsCard({ orgId }: { orgId: string }) {
  const nav = useNavigate();
  const { data: windows = [] } = useScratchWindows(orgId);
  const { data: roster } = useDutyRoster(orgId, 16);
  const create = useCreateScratchWindow(orgId);
  const scratchWeeks = (roster?.weeks ?? []).filter((x) => x.rostered && !x.dutyTeamId).map((x) => x.periodStart);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('Christmas scratch weeks');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [replyBy, setReplyBy] = useState('');
  useEffect(() => {
    if (!from && scratchWeeks.length) {
      setFrom(scratchWeeks[0]!); setTo(scratchWeeks[scratchWeeks.length - 1]!);
      setReplyBy(addDays(scratchWeeks[0]!, -14));
    }
  }, [scratchWeeks.join()]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{ ...card, marginTop: 20 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: theme.text }}>Scratch weeks</div>
      <div style={{ fontSize: 12, color: theme.textMuted, marginBottom: 8 }}>
        Weeks with no team (mark them "Scratch week" above). Ask everyone when they can help, then build the crews and UDOs from the answers.
      </div>
      {windows.map((x) => (
        <button key={x.id} onClick={() => nav(`/scratch/${x.id}`)} style={rowBtn}>
          <span style={{ flex: 1 }}>
            <strong style={{ color: theme.text }}>{x.title}</strong>
            <span style={{ fontSize: 12, color: theme.textMuted }}> · {shortDate(x.fromWeek)} – {shortDate(addDays(x.toWeek, 7))} · {x.publishedAt ? 'published' : `replies by ${fmtDate(x.replyBy)}`}</span>
          </span>
          <Icon name="chevron" size={14} />
        </button>
      ))}
      {!open
        ? <button onClick={() => setOpen(true)} disabled={!scratchWeeks.length} style={{ ...secondarySm, marginTop: 8 }}>
            {scratchWeeks.length ? 'Ask for availability' : 'No scratch weeks in the next 16 weeks'}
          </button>
        : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
            <input value={title} onChange={(e) => setTitle(e.target.value)} aria-label="Title" style={select} />
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <label style={field}>From week
                <select value={from} onChange={(e) => setFrom(e.target.value)} style={select}>{scratchWeeks.map((x) => <option key={x} value={x}>{shortDate(x)}</option>)}</select>
              </label>
              <label style={field}>To week
                <select value={to} onChange={(e) => setTo(e.target.value)} style={select}>{scratchWeeks.filter((x) => x >= from).map((x) => <option key={x} value={x}>{shortDate(x)}</option>)}</select>
              </label>
              <label style={field}>Reply by
                <input type="date" value={replyBy} onChange={(e) => setReplyBy(e.target.value)} style={select} />
              </label>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button disabled={create.isPending || !from || !to || !replyBy} style={primarySm} onClick={() => create.mutate(
                { title: title.trim(), fromWeek: from, toWeek: to, replyBy: new Date(`${replyBy}T18:00:00`).toISOString() },
                { onSuccess: (r) => nav(`/scratch/${r.id}`), onError: (e) => emitError(e instanceof Error ? e.message : 'Could not open it') },
              )}>{create.isPending ? 'Opening…' : 'Open & tell everyone'}</button>
              <button onClick={() => setOpen(false)} style={secondarySm}>Cancel</button>
            </div>
          </div>
        )}
    </div>
  );
}

/** My shifts: a nudge for open windows I haven't replied to. */
export function ScratchNudge({ orgId }: { orgId: string }) {
  const nav = useNavigate();
  const { data: windows = [] } = useScratchWindows(orgId);
  const todo = windows.filter((x) => !x.myReplied && !x.publishedAt && new Date(x.replyBy) > new Date());
  if (!todo.length) return null;
  return (
    <>
      {todo.map((x) => (
        <button key={x.id} onClick={() => nav(`/scratch/${x.id}`)} style={{
          margin: '12px 16px 0', padding: '10px 12px', borderRadius: 10, border: 'none', cursor: 'pointer', textAlign: 'left',
          width: 'calc(100% - 32px)', background: theme.status.service.bg, color: theme.status.service.fg, fontFamily: theme.font, fontSize: 13,
        }}>
          <strong>{x.title}:</strong> tell us which shifts you could do — by {fmtDate(x.replyBy)}. →
        </button>
      ))}
    </>
  );
}

const card: React.CSSProperties = { padding: '12px 14px', borderRadius: 12, background: theme.surface, border: `1px solid ${theme.border}` };
const muted: React.CSSProperties = { fontSize: 13, color: theme.textMuted };
const field: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 600, color: theme.textDim };
const select: React.CSSProperties = {
  padding: '7px 9px', fontSize: 13, borderRadius: 8, border: `1px solid ${theme.border}`,
  background: theme.surface, color: theme.text, fontFamily: theme.font, minWidth: 0, boxSizing: 'border-box',
};
const primary: React.CSSProperties = {
  padding: '12px 0', borderRadius: 12, border: 'none', fontSize: 15, fontWeight: 700,
  fontFamily: theme.font, background: theme.accent, color: theme.accentInk, cursor: 'pointer',
};
const primarySm: React.CSSProperties = {
  padding: '7px 12px', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 700,
  fontFamily: theme.font, background: theme.accent, color: theme.accentInk, cursor: 'pointer',
};
const secondarySm: React.CSSProperties = {
  padding: '7px 12px', borderRadius: 8, border: `1px solid ${theme.border}`, fontSize: 13, fontWeight: 700,
  fontFamily: theme.font, background: theme.surface, color: theme.text, cursor: 'pointer',
};
const iconBtn: React.CSSProperties = {
  width: 36, height: 36, border: `1px solid ${theme.border}`, background: theme.surface2, color: theme.text,
  borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0,
};
const chipX: React.CSSProperties = {
  width: 18, height: 18, borderRadius: 9, border: 'none', background: theme.surface, cursor: 'pointer',
  display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.textMuted, padding: 0,
};
const rowBtn: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 0', background: 'none', border: 'none',
  borderTop: `1px solid ${theme.border}`, textAlign: 'left', cursor: 'pointer', fontFamily: theme.font, fontSize: 13,
};
